import posthog from "posthog-js";
import type { AnalyticsEventName } from "./events";
import type { AnalyticsAdapter, AnalyticsProperties } from "./types";
import { isNoticeProductOnly, redactNoticeCapture, redactNoticeProperties } from "./noticePrivacy";
import { sanitizeCampaignUrl } from "./webCampaignAttribution";

export function createPosthogAdapter(params: {
  enabled: boolean;
  apiKey: string;
  apiHost: string;
  productOnly?: () => boolean;
}): AnalyticsAdapter {
  let initialized = false;
  const productOnly = params.productOnly ?? isNoticeProductOnly;

  return {
    destination: "product",
    init() {
      if (initialized || !params.enabled || !params.apiKey || !params.apiHost || typeof window === "undefined") {
        return;
      }

      posthog.init(params.apiKey, {
        api_host: params.apiHost,
        autocapture: false,
        capture_pageview: false,
        before_send: (event) => {
          if (!event?.properties) return event;
          const urlKeys = [
            "$current_url",
            "$initial_current_url",
            "$referrer",
            "$initial_referrer",
            "current_url",
            "initial_current_url",
            "referrer",
            "url",
          ];
          for (const key of urlKeys) {
            const value = event.properties[key];
            if (typeof value === "string") {
              event.properties[key] = sanitizeCampaignUrl(value, window.location.origin);
            }
          }
          for (const key of ["path", "landing_path"]) {
            const value = event.properties[key];
            if (typeof value === "string") {
              event.properties[key] = sanitizeCampaignUrl(value, window.location.origin).replace(window.location.origin, "");
            }
          }
          // Evaluate notice policy at send time as SDK defaults and queued
          // events can bypass the app's semantic-event scrubber.
          return productOnly() || event.properties.analytics_purpose === "service_period_notice"
            ? redactNoticeCapture(event, params.apiKey) : event;
        },
      });
      initialized = true;
    },
    track(name: AnalyticsEventName, properties: AnalyticsProperties = {}) {
      if (!params.enabled) {
        return;
      }

      posthog.capture(name, productOnly()
        ? { ...redactNoticeProperties(properties), analytics_purpose: "service_period_notice" }
        : properties);
    },
    identify(userId: string, properties: AnalyticsProperties = {}) {
      if (!params.enabled || !userId) {
        return;
      }

      posthog.identify(userId, productOnly() ? redactNoticeProperties(properties) : properties);
    },
    reset() {
      if (!params.enabled) {
        return;
      }

      posthog.reset();
    },
  };
}
